/* eslint-disable react/require-default-props */
// This is a forked version of frontend/packages/portal-frontend/src/plot/components/ScatterPlot.tsx
// The idea is this one can be used for experimental changes.
import React, { useEffect, useMemo, useRef, useState } from "react";
import type Plotly from "plotly.js";
import type {
  Config,
  Layout,
  PlotData,
  PlotlyHTMLElement,
  PlotMouseEvent,
  PlotSelectionEvent,
} from "plotly.js";
import { usePlotlyLoader } from "../../../../../contexts/PlotlyLoaderContext";
import { MAX_POINTS_TO_ANNOTATE } from "../../../../../constants/plotConstants";
import {
  AnnotationTail,
  applyAnnotationTailOverrides,
  applyLegendLabelOverrides,
  calcAnnotationArrowWidth,
  calcAnnotationPositions,
  calcAutoscaleShapes,
  calcChromeAxisOverrides,
  calcPlotIndicatorLineShapes,
  captureAnnotationTail,
  categoricalDataToValueCounts,
  cloneFigureForExport,
  countExclusivelyTrueValues,
  countInclusivelyTrueValues,
  DataExplorerColorPalette,
  DEFAULT_CHROME_LINE_WIDTH,
  DEFAULT_DATA_LINE_WIDTH,
  DEFAULT_PALETTE,
  EXPORT_EDGE_PADDING,
  exportMarginFloor,
  getLegendTraces,
  getRange,
  legendLayoutFor,
  wantsLegendTraces,
  hexToRgba,
  LEGEND_REMAINDER,
  LegendInfo,
  LegendKey,
  NEUTRAL_FACET_FILL,
  onWebglContextRestored,
  orderContinuousPointsByBin,
  releaseWebglContexts,
  resolveAnnotationTail,
  RegressionLine,
  widenExtentsToAxisRange,
} from "./plotUtils";
import usePlotResizer from "./usePlotResizer";
import installFacetSelectionDragLayer, {
  FacetSelectionConfig,
  resolveRange,
} from "./facetSelectionDragLayer";
import type ExtendedPlotType from "../../../ExtendedPlotType";
import styles from "../../../styles/ScatterPlot.scss";

type Data = Record<string, any>;

interface Props {
  data: Data;
  xKey: string;
  yKey: string;
  xLabel: string;
  yLabel: string;
  // Height can be defined in pixels or set to "auto."  In auto mode, it will
  // attempt to fill the height of the viewport.
  height: number | "auto";
  legendForDownload?: LegendInfo;
  // If defined, the corresponding key from `data` will used to generate hover
  // text.
  hoverTextKey?: string;
  // Allows you to specify which key of `data` should be used to label annotations.
  // If this is not defined, it will try to use `hoverTextKey` instead. If that's
  // not defined, it will use (x, y) values to annotate points.
  annotationTextKey?: string;
  colorKey1?: string;
  colorKey2?: string;
  categoricalColorKey?: string;
  continuousColorKey?: string;
  contLegendKeys?: LegendKey[] | null;
  colorMap: Map<LegendKey, string>;
  selectedPoints?: Set<number>;
  // Special case. Set to false to avoid outlining the VERY densely packed Waterfall plot points.
  outlineUnselectedPoints?: boolean;
  onClickPoint?: (pointIndex: number, ctrlKey: boolean) => void;
  onMultiselect?: (pointIndices: number[]) => void;
  onClickResetSelection?: () => void;
  pointVisibility?: boolean[];
  showIdentityLine?: boolean;
  regressionLines?: RegressionLine[] | null;
  onLoad?: (plot: ExtendedPlotType) => void;
  customHoverinfo?: PlotData["hoverinfo"];
  hideXAxis?: boolean;
  hideXAxisGrid?: boolean;
  // True only for Waterfall (which renders through this same component):
  // its x "value" is a rank position, not real data, so once a genuine
  // title override replaces the default "Rank" text, the tick numbers
  // below it are dropped too (see getImageFigure). A real scatter plot's
  // x-axis is actual data — those numbers stay no matter what the title
  // says — so this must default to false for every other caller.
  xAxisIsRank?: boolean;
  showBuiltinLegend?: boolean;
  // optional styling
  pointSize?: number;
  pointOpacity?: number;
  outlineWidth?: number;
  palette?: DataExplorerColorPalette;
  // Point labels. Their own setting rather than a ratio of the axis
  // font: these sit inside the plot area competing with the data.
  annotationFontSize?: number;
  xAxisFontSize?: number;
  yAxisFontSize?: number;
  // When true, box/lasso selection is replaced with a custom drag whose marquee
  // is confined to the facet region it starts in (and cannot escape into a
  // neighbouring facet). `selectionRegions` describes those regions along
  // `selectionAxis` (waterfall uses the x-axis rank regions). See
  // facetSelectionDragLayer.
  enforceSingleFacetSelection?: boolean;
  selectionRegions?:
    | { key: string | number | symbol; lo: number; hi: number }[]
    | null;
  selectionAxis?: "x" | "y";
  // See PrototypeDensity1D. Label precisely the contacted points; diverges from
  // `selectedPoints` only under facet_by === "expansion" (the waterfall path).
  // Falls back to `selectedPoints` when omitted, so the non-faceted scatter
  // path (which passes neither prop) is unchanged.
  pointsToAnnotate?: Set<number>;
  selectionCount?: number;
  // Whether facet_by has real backing for this plot (e.g. waterfall's
  // x-clustering). When true and color has nothing of its own to show
  // (!hasColorOptionsEnabled), the default trace renders neutral instead of
  // palette.all — see PrototypeDensity1D's analogous hasRealFacetBacking.
  // Defaults to false: the non-faceted scatter call site never has a real
  // facet_by to report here (that case already routes to
  // SmallMultiplesScatter instead of this component).
  hasFacetOptionsEnabled?: boolean;
  // Transient view state to start from, instead of this instance building its
  // own up from scratch. Both are normally accumulated by interacting with the
  // plot (zooming, dragging a label's tail) and held in instance-local refs,
  // so a second instance of the same plot — the export preview — would
  // otherwise open autoranged with every label back at its default position.
  // Produce these with `captureTransientState` against the live plot.
  initialAxes?: Partial<Layout>;
  initialAnnotationTails?: Record<string, AnnotationTail>;
}

type PlotlyType = typeof Plotly;
type PropsWithPlotly = Props & { Plotly: PlotlyType };

const calcPlotHeight = (plot: HTMLDivElement) => {
  if (window.innerWidth < 900) {
    return 600;
  }

  return window.innerHeight - plot.offsetTop - 22;
};

const byValueCountOf = (counts: Map<string | symbol | null, number>) => (
  a: string | symbol,
  b: string | symbol
) => {
  const countA = counts.get(a) || 0;
  const countB = counts.get(b) || 0;

  if (countA === countB) {
    return 0;
  }

  return countA < countB ? -1 : 1;
};

// HACK: Selected points get a black outline and this looks weird when
// regression lines sit on top of them. We'll do some hacky DOM manipulation to
// change the stacking order.
const moveSelectedPointsOnTopOfLines = (plot: HTMLDivElement) => {
  const modebar = plot.querySelector(".modebar-container") as HTMLElement;
  const focusCanvas = plot.querySelector(".gl-canvas-focus") as HTMLElement;

  // `focusCanvas` is the canvas that has the focused (i.e. selected) points.
  // We'll move that just before the modebar in the DOM, which actually puts it
  // right after the shapes (i.e. y=x and regression lines).
  modebar.parentElement!.insertBefore(focusCanvas, modebar);

  // That's almost exactly what we want but now those points can appear on top
  // of annotations (i.e. point labels). We'll move those so they're a little
  // later in the DOM, right before the hover information.
  const infolayer = plot.querySelector(".infolayer") as HTMLElement;
  const hoverlayer = plot.querySelector(".hoverlayer") as HTMLElement;
  hoverlayer.parentElement!.insertBefore(infolayer, hoverlayer);

  // Also nudge the hover label slightly to the right. It can sometimes get in
  // the way when trying to click on a point.
  hoverlayer.style.transform = "translateX(8px)";
};

function PrototypeScatterPlot({
  data,
  xKey,
  yKey,
  xLabel,
  yLabel,
  height,
  colorKey1,
  colorKey2,
  legendForDownload,
  categoricalColorKey,
  continuousColorKey,
  contLegendKeys,
  colorMap,
  hoverTextKey = undefined,
  annotationTextKey = undefined,
  selectedPoints = undefined,
  pointVisibility = undefined,
  showIdentityLine = false,
  regressionLines = undefined,
  outlineUnselectedPoints = true,
  onClickPoint = () => {},
  onMultiselect = () => {},
  onClickResetSelection = () => {},
  onLoad = () => {},
  customHoverinfo = undefined,
  hideXAxis = false,
  hideXAxisGrid = false,
  xAxisIsRank = false,
  showBuiltinLegend = false,
  pointSize = 7,
  pointOpacity = 1.0,
  outlineWidth = 0.5,
  palette = DEFAULT_PALETTE,
  annotationFontSize = 12,
  xAxisFontSize = 14,
  yAxisFontSize = 14,
  enforceSingleFacetSelection = false,
  selectionRegions = null,
  selectionAxis = "x",
  pointsToAnnotate,
  selectionCount,
  hasFacetOptionsEnabled = false,
  initialAxes = undefined,
  initialAnnotationTails = undefined,
  Plotly,
}: PropsWithPlotly) {
  const ref = useRef<ExtendedPlotType>(null);
  usePlotResizer(Plotly, ref);

  // We save the axes of the plot so we can keep the zoom level consistent
  // between calls to Plotly#react. This value is upated
  // - When the plot is first rendered (and an autorange is calculated)
  // - After each plotly_relayout event (e.g. when the user changes the zoom
  // level).
  const axes = useRef<Partial<Layout>>(
    initialAxes ?? {
      xaxis: undefined,
      yaxis: undefined,
    }
  );

  const extents = useMemo(() => {
    const [minX, maxX] = getRange(data[xKey] as number[]);
    const [minY, maxY] = getRange(data[yKey] as number[]);

    const rangeX = maxX - minX;
    const rangeY = maxY - minY;
    const ratio = Math.min(rangeX, rangeY) / Math.max(rangeX, rangeY);

    return { minX, maxX, minY, maxY, rangeX, rangeY, ratio };
  }, [data, xKey, yKey]);

  const shapes = useMemo(() => {
    return calcPlotIndicatorLineShapes(
      showIdentityLine,
      regressionLines,
      extents,
      true
    );
  }, [showIdentityLine, regressionLines, extents]);

  const annotationTails = useRef<Record<string, AnnotationTail>>(
    initialAnnotationTails ? { ...initialAnnotationTails } : {}
  );

  const [dragmode, setDragmode] = useState<Layout["dragmode"]>("zoom");

  // On mount, we call the `onLoad` callback with a reference to the DOM node
  // (which is extended with convenience functions).
  useEffect(() => {
    if (onLoad && ref.current) {
      onLoad(ref.current);
    }
  }, [onLoad]);

  useEffect(() => {
    const plot = ref.current;
    return () => {
      (plot as any)?.__facetSelCleanup?.();
      releaseWebglContexts(plot as HTMLElement);
      Plotly.purge(plot as HTMLElement);
    };
  }, [Plotly]);

  // When the columns or underlying data change, we force an autoscale by
  // discarding the stored axes.
  const hasEverRun = useRef(false);

  useEffect(() => {
    // Skipped on mount so this doesn't immediately discard `initialAxes`.
    // Effects always run once, and there is nothing to autoscale away from on
    // a first render anyway: either we were seeded (and want to keep it) or
    // the ref is already empty.
    if (!hasEverRun.current) {
      hasEverRun.current = true;
      return;
    }

    axes.current = {
      xaxis: undefined,
      yaxis: undefined,
    };
  }, [xKey, yKey, xLabel, yLabel, data]);

  // Update axes when font size changes.
  useEffect(() => {
    const xaxis = axes.current.xaxis;
    const yaxis = axes.current.yaxis;

    if (xaxis) {
      xaxis.tickfont = { size: xAxisFontSize };
      xaxis.title = {
        ...(xaxis.title as object),
        font: { size: xAxisFontSize },
      };
    }

    if (yaxis) {
      yaxis.tickfont = { size: yAxisFontSize };
      yaxis.title = {
        ...(yaxis.title as object),
        font: { size: yAxisFontSize },
      };
    }
  }, [xAxisFontSize, yAxisFontSize]);

  // All other updates are handled by this one big effect.
  useEffect(() => {
    const plot = ref.current as ExtendedPlotType;

    const x = data[xKey] as number[];
    const y = data[yKey] as number[];
    const text = hoverTextKey ? data[hoverTextKey] : x.map(() => "");
    const annotationText = annotationTextKey
      ? data[annotationTextKey]
      : x.map(
          (_: any, i: number) => `${x[i]?.toFixed(2)}, ${y[i]?.toFixed(2)}`
        );
    const visible = pointVisibility ?? x.map(() => true);

    const color1 = colorKey1 ? data[colorKey1] : null;
    const color2 = colorKey2 ? data[colorKey2] : null;
    const catColorData = categoricalColorKey ? data[categoricalColorKey] : null;
    const catColorValueCounts = categoricalDataToValueCounts(
      catColorData?.map((value: string, i: number) =>
        x[i] !== null && y[i] !== null ? value : null
      ),
      visible
    );
    const contColorData: (number | null)[] = continuousColorKey
      ? data[continuousColorKey]
      : null;
    const hasColorOptionsEnabled = Boolean(
      color1 || color2 || catColorData || contColorData
    );
    // Only takes effect when templateTrace is actually used as the default
    // (uncolored) trace below — color having its own real categories always
    // wins, this never overrides a real color. See PrototypeDensity1D's
    // analogous hasRealFacetBacking/inertColor.
    const inertColor = hasFacetOptionsEnabled
      ? NEUTRAL_FACET_FILL
      : palette.all;

    const isSelectionMode =
      dragmode === "select" ||
      dragmode === "lasso" ||
      (selectedPoints && selectedPoints.size > 0);

    // When enforceSingleFacetSelection is on, there is more than one region to
    // constrain across, and a select/lasso tool is active, we let Plotly run its
    // real selection but install facetSelectionDragLayer, whose pointer
    // interceptor confines the native box/lasso to the region the drag started
    // in. So the real `dragmode` passes through (previously we disabled Plotly's
    // drag and drew a custom marquee). Otherwise behavior is unchanged.
    const useFacetSelection =
      enforceSingleFacetSelection &&
      Boolean(selectionRegions && selectionRegions.length > 1) &&
      (dragmode === "select" || dragmode === "lasso");
    const groupSelectionTool = dragmode === "lasso" ? "lasso" : "box";

    const templateTrace = {
      type: "scattergl" as const,
      mode: "markers" as const,
      // HACK: Settings coordinates to `null` is the most performant way to
      // hide them. We make sure to do this for both axes because it affects
      // autoscaling.
      x: x.map((value: number, i) =>
        visible[i] &&
        // Make sure there's a value on the opposite axis, otherwise an
        // unplottable outlier could affect the axis scale.
        y[i] !== null
          ? value
          : null
      ),
      y: y.map((value: number, i) =>
        visible[i] &&
        // Same idea as above.
        x[i] !== null
          ? value
          : null
      ),
      name: "",
      text,
      hoverinfo: customHoverinfo || ("x+y+text" as const),
      showlegend: false,
      hoverlabel: { bgcolor: inertColor },
      selectedpoints: selectedPoints ? [...selectedPoints] : [],
      marker: {
        color: hexToRgba(inertColor, pointOpacity),
        size: pointSize,
        line: { color: inertColor, width: outlineWidth },
      },
      selected: { marker: { opacity: 1 } },
      unselected: {
        marker: {
          opacity: isSelectionMode ? 0.5 : 1,
        },
      },
    };

    const makeColorTrace = (
      color: string,
      hasTargetColor: (i: number) => boolean
    ) => {
      return {
        ...templateTrace,
        marker: {
          ...templateTrace.marker,
          line: templateTrace.marker.line
            ? {
                ...templateTrace.marker.line,
                color,
              }
            : undefined,
          color: hexToRgba(color, pointOpacity),
        },
        hoverlabel: { bgcolor: color },
        x: x.map((value: number, i) => {
          return visible[i] && hasTargetColor(i) ? value : null;
        }),
      };
    };

    const defaultTrace = hasColorOptionsEnabled ? null : templateTrace;

    const color1Trace = color1
      ? makeColorTrace(palette.compare1, (i) => color1[i] && !color2?.[i])
      : null;

    const color2Trace = color2
      ? makeColorTrace(palette.compare2, (i) => color2[i] && !color1?.[i])
      : null;

    const colorOverlapTrace =
      color1 && color2
        ? makeColorTrace(palette.compareBoth, (i) => color1[i] && color2[i])
        : null;

    const comparisonColorTraces = [
      [color1Trace, countExclusivelyTrueValues(color1, color2, visible)],
      [color2Trace, countExclusivelyTrueValues(color2, color1, visible)],
      [colorOverlapTrace, countInclusivelyTrueValues(color1, color2, visible)],
    ]
      .filter(([trace]) => trace)
      .sort(([, a], [, b]) => (a! < b! ? -1 : 1))
      .map(([trace]) => trace);

    // TODO: Instead of making a trace for each category and sorting those
    // traces, we could sort the individual points instead (much more
    // performant). That comes with its own issues (selection behavior is
    // becomes buggy when points are out of order) but we've already solved
    // that for `continuousColorTrace` below.
    let categoricalColorTraces: Partial<PlotData>[] = [];

    if (colorMap && catColorData) {
      const catCardinality = Object.keys(colorMap || {}).length;

      // Categories that didn't earn their own color are drawn as one bucket,
      // and it must never occlude a category that did. `plotlyData` is reversed
      // below, so the LAST entry here is drawn first — at the bottom. That is
      // also why the count sort is ascending: smallest last, on top.
      //
      // The bucket cannot be placed by that sort, being a symbol with no count
      // of its own, so it is appended explicitly. It sits directly above the
      // null/N-A trace, which the outer array already keeps below everything.
      const shownCategories = new Set(
        [...colorMap.keys()].filter((key) => typeof key === "string")
      );

      const remainderTrace = colorMap.has(LEGEND_REMAINDER)
        ? [
            makeColorTrace(
              colorMap.get(LEGEND_REMAINDER)!,
              (i) =>
                catColorData[i] !== null &&
                !shownCategories.has(catColorData[i] as string)
            ),
          ]
        : [];

      categoricalColorTraces =
        catCardinality < 75
          ? [
              ...[...colorMap.keys()]
                .filter((key) => key !== LEGEND_REMAINDER)
                .sort(byValueCountOf(catColorValueCounts))
                .filter((key) => colorMap.get(key))
                .map((key) =>
                  makeColorTrace(
                    colorMap.get(key)!,
                    (i) => key === catColorData[i]
                  )
                ),
              ...remainderTrace,
            ]
          : // WORKAROUND: If there's a large number of categories,
            // make a trace for each color instead. This is worse
            // for the stacking order but better for performance.
            [...new Set(colorMap.values())].map((color) =>
              makeColorTrace(
                color!,
                (i) => color === colorMap.get(catColorData[i])
              )
            );
    }

    // TODO: Add support for palette.divergingScale
    let continuousColorTrace: any = null;

    // HACK: The continuous trace gets its own index. That is, its points are
    // mixed up relative to the other traces. This creates much confusion...
    // but it's worth it. It allows us to plot points from the bins with the
    // fewest values on top. This makes the plot much more interpretable. But
    // it means that when we click or select a point, we have to figure out
    // what index it originally came from.
    const contTraceIndex: number[] = [];

    if (contColorData && contLegendKeys) {
      const sortedX: (number | null)[] = [];
      const sortedY: (number | null)[] = [];
      const sortedColor: (number | null)[] = [];
      const hoverColor: string[] = [];
      const sortedText: string[] = [];
      const sortedAnnotationText: string[] = [];
      const remappedSelectedPoints: number[] = [];

      const order = orderContinuousPointsByBin(
        contColorData,
        contLegendKeys,
        colorMap,
        visible
      );

      order.forEach((origIndex, i) => {
        contTraceIndex.push(origIndex);
        sortedX.push(templateTrace.x[origIndex]);
        sortedY.push(templateTrace.y[origIndex]);
        sortedColor.push(contColorData[origIndex]);
        hoverColor.push(colorMap.get(contLegendKeys[origIndex])!);
        sortedText.push(text[origIndex]);
        sortedAnnotationText.push(annotationText[origIndex]);
        if (selectedPoints?.has(origIndex)) {
          remappedSelectedPoints.push(i);
        }
      });

      continuousColorTrace = {
        ...templateTrace,
        x: sortedX,
        y: sortedY,
        text: sortedText,
        annotationText: sortedAnnotationText,
        selectedpoints: remappedSelectedPoints,
        hoverlabel: { bgcolor: hoverColor },
        marker: {
          size: pointSize,
          color: sortedColor.map((c: number | null) => c ?? "transparent"),
          colorscale: palette.sequentialScale.map(([value, color]) => [
            value,
            hexToRgba(color, pointOpacity),
          ]),
          line: {
            width: outlineWidth,
            color: sortedColor.map((c: number | null) => c ?? "transparent"),
            colorscale: palette.sequentialScale,
          },
        },
      };
    }

    let otherPointsTrace = null;

    if (color1 || color2) {
      otherPointsTrace = makeColorTrace(
        palette.other,
        (i) => !color1?.[i] && !color2?.[i]
      );
    }

    if (catColorData) {
      otherPointsTrace = makeColorTrace(
        palette.other,
        (i) => catColorData[i] === null
      );
    }

    if (contColorData) {
      otherPointsTrace = makeColorTrace(
        palette.other,
        (i) => contColorData[i] === null
      );
    }

    // WORKAROUND: We use a special trace to give selected
    // points a dark outline, due to limitations of Plotly.
    const selectionOutlineTrace = {
      ...templateTrace,
      marker: {
        color: "transparent",
        size: pointSize + outlineWidth * 2,
        line: { color: "#000", width: Math.max(outlineWidth, 2) },
      },
      selected: { marker: { opacity: 1 } },
      unselected: { marker: { opacity: 0 } },
      hoverinfo: "skip",
    };

    const plotlyData = [
      defaultTrace,
      ...comparisonColorTraces,
      ...categoricalColorTraces,
      continuousColorTrace,
      otherPointsTrace,
      selectionOutlineTrace,
    ]
      .filter(Boolean)
      .reverse() as Partial<PlotData>[];

    if (showBuiltinLegend) {
      getLegendTraces(legendForDownload!, templateTrace).forEach((t) =>
        plotlyData.push(t as typeof plotlyData[number])
      );
    }

    const isContinuousCurve = (curveNumber: number) => {
      return plotlyData[curveNumber] === continuousColorTrace;
    };

    // Restore or initialize axes. We set `autorange` to true on the first render
    // so that Plotly can calculate the extents of the plot for us.
    // `automargin` measures the rendered tick labels and grows the margin to
    // fit them. That's the only way to get this right: the space a y axis needs
    // depends on how many characters its widest tick label has ("-0.00123" vs
    // "5"), which is a property of the data, not of the font size — so the
    // arithmetic this replaces could never work for it. The x axis fared better
    // only because vertical space really is roughly linear in font size.
    //
    // It also fixes a second, quieter bug: Plotly derives the axis title's
    // offset from `ax._depth`, the measured extent of the tick labels, and
    // `_depth` is only computed when automargin is on. Without it the title
    // sits a constant distance from the axis line and can land on top of wide
    // tick labels. The title's own space is reserved too (plotly's
    // axes.js adds approxTitleDepth + standoff to the push).
    //
    // Set outside the cache lookup so it also applies to a restored axis, or
    // one seeded by captureTransientState from a plot rendered before this.
    const xaxis = {
      automargin: true,
      ...(axes.current.xaxis || {
        title: {
          text: xLabel,
          font: { size: xAxisFontSize },
          standoff: 8,
        } as any,
        tickfont: { size: xAxisFontSize },
        exponentformat: "e",
        type: "linear",
        autorange: true,
        visible: !hideXAxis,
        showgrid: !hideXAxisGrid,
      }),
    };

    const yaxis = {
      automargin: true,
      ...(axes.current.yaxis || {
        title: {
          text: yLabel,
          font: { size: yAxisFontSize },
          standoff: 0,
        } as any,
        tickfont: { size: yAxisFontSize },
        exponentformat: "e",
        autorange: true,
      }),
    };

    // Annotation channel (expansion-selection): label precisely the contacted
    // points, not the re-expanded `selectedPoints`. They coincide off the
    // facet_by === "expansion" collapse (the only collapse path here is the
    // waterfall), so this is a no-op for the plain scatter, which passes
    // neither prop and falls back to `selectedPoints`.
    const pointsForAnnotation = pointsToAnnotate ?? selectedPoints;
    // Count for the "too many to label" fallback. selectionCount is the model
    // count under collapse (selection.size, threaded from the wrapper). TODO:
    // the noun "points" should become the index type's display name (e.g.
    // "models") once that is threaded through; literal for now.
    const annotationCount = selectionCount ?? selectedPoints?.size ?? 0;

    const layout: Partial<Layout> = {
      dragmode,

      // enforceSingleFacetSelection: a sweep within a narrow x-region is mostly
      // vertical, which Plotly's default selectdirection "any" auto-reads as a
      // "v" (full-x-axis) selection spanning every region — set programmatically,
      // so the pointer clamp can't catch it. Force diagonal-only ("d") so every
      // box stays an ordinary drag-cornered rectangle; the interceptor then
      // clamps its width to the anchor region.
      selectdirection: useFacetSelection ? "d" : "any",
      // Actual shapes are drawn in the "plotly_afterplot" event handler.
      // That's to prevent them having any effect on autoscaling. However, we
      // *do* want to force the scales to match when `showIdentityLine` is
      // true. That's what `calcAutoscaleShapes()` is for. It's a hak that adds
      // an invisible line that tricks Plotly into adjusting the scale.
      shapes: calcAutoscaleShapes(showIdentityLine, extents),
      height: height === "auto" ? calcPlotHeight(plot) : height,

      margin: {
        t: 30,
        r: 30,
        // A floor, not a guess: automargin grows these as the labels and
        // titles require.
        b: 50,
        l: 50,
      },
      hovermode: "closest",

      // We hide the legend because the traces don't have names and some of
      // them are merely decorative (e.g. selectionOutlineTrace). DE2 has its
      // own custom-build legend so this isn't a problem.
      showlegend: true,

      xaxis,
      yaxis,

      annotations:
        pointsForAnnotation &&
        pointsForAnnotation.size <= MAX_POINTS_TO_ANNOTATE
          ? [...pointsForAnnotation]
              .filter(
                // Filter out any annotations associated with missing data. This can
                // happen if the x or y column has changed since the annotations were
                // created.
                (pointIndex) =>
                  typeof x[pointIndex] === "number" &&
                  typeof y[pointIndex] === "number"
              )
              .map((pointIndex) => {
                // Restore any annotation arrowhead position the user may
                // have edited, rescaled for the current annotationFontSize
                // — see resolveAnnotationTail.
                const tail = resolveAnnotationTail(
                  annotationTails.current[`${xKey}-${yKey}-${pointIndex}`],
                  annotationFontSize
                );

                return {
                  x: x[pointIndex],
                  y: y[pointIndex],
                  text: annotationText[pointIndex],
                  visible: visible[pointIndex],
                  xref: "x",
                  yref: "y",
                  arrowhead: 0,
                  arrowwidth: calcAnnotationArrowWidth(annotationFontSize),
                  standoff: 4,
                  font: { size: annotationFontSize },
                  arrowcolor: "#888",
                  bordercolor: "#c7c7c7",
                  bgcolor: "#fff",
                  pointIndex,
                  ax: tail?.ax,
                  ay: tail?.ay,
                };
              })
          : (() => {
              return selectedPoints
                ? [
                    {
                      text: `(${annotationCount} selected points)`,
                      font: { size: annotationFontSize },
                      arrowcolor: "transparent",
                      bordercolor: "#c7c7c7",
                      bgcolor: "#fff",
                      xref: "paper",
                      yref: "paper",
                    },
                  ]
                : undefined;
            })(),
    };

    const config: Partial<Config> = {
      // Automatically resizes the plot when the window is resized.
      responsive: true,

      // Allows the user to move annotations (but just the tail and not the
      // whole thing).
      edits: { annotationTail: true },

      displaylogo: false,
      modeBarButtonsToRemove: ["select2d", "lasso2d"],
    };

    Plotly.react(plot, plotlyData, layout, config);

    // enforceSingleFacetSelection: hand the live config to the custom drag
    // layer and (re)install it. Mirrors PrototypeDensity1D, but the regions are
    // explicit x-rank ranges (waterfall) rather than violin tracks. The config
    // is read on each mousedown so it stays current across effect re-runs.
    {
      const prev = (plot as any).__facetSel as FacetSelectionConfig | undefined;
      const resetAdditive =
        !prev ||
        prev.tool !== groupSelectionTool ||
        prev.enabled !== useFacetSelection;

      if (prev && resetAdditive) {
        prev.committedShapes = [];
        prev.selectionRegionKey = null;
      }

      (plot as any).__facetSel = {
        enabled: useFacetSelection,
        tool: groupSelectionTool,
        axis: selectionAxis,
        regionModel: {
          kind: "ranges",
          regions: selectionRegions ?? [],
        },
        committedShapes: resetAdditive ? [] : prev!.committedShapes,
        selectionRegionKey: resetAdditive ? null : prev!.selectionRegionKey,
      } as FacetSelectionConfig;

      installFacetSelectionDragLayer(plot as any);
    }

    // Keep track of added listeners so we can easily remove them.
    const listeners: [string, (e: any) => void][] = [];

    const on = (eventName: string, callback: (e: any) => void) => {
      plot.on(
        eventName as Parameters<PlotlyHTMLElement["on"]>[0],
        callback as Parameters<PlotlyHTMLElement["on"]>[1]
      );
      listeners.push([eventName, callback]);
    };

    // HACK: The zoom functions provided by Plotly's modebar aren't exposed
    // by its API. The only way to trigger them is by actually clicking the
    // buttons 😕
    const getButton = (attr: string, val: string) =>
      plot.querySelector(
        `.modebar-btn[data-attr="${attr}"][data-val="${val}"]`
      ) as HTMLAnchorElement;

    const zoom = (val: "in" | "out" | "reset") => {
      getButton("zoom", val).click();
    };

    const assignAnnotationPositions = (pointIndices: number[]) => {
      // Don't both doing the calculation if we're not going to show them.
      if (new Set(pointIndices).size > MAX_POINTS_TO_ANNOTATE) {
        return;
      }

      const fullLayout = (plot as any)._fullLayout;

      calcAnnotationPositions(x, y, pointIndices, fullLayout).forEach(
        ({ pointIndex, ax, ay }) => {
          annotationTails.current[
            `${xKey}-${yKey}-${pointIndex}`
          ] = captureAnnotationTail(ax, ay, annotationFontSize);
        }
      );
    };

    plot.annotateSelected = (points?: number[]) => {
      // Position annotation tails for the given points (the context path passes
      // its representative points; selection + annotation state is already set
      // by setSelectionFromContext). Falls back to the current selection when
      // called with no argument. No longer commits a selection of its own —
      // that "annotate == select" coupling is now split into the two channels.
      const pts = points ?? (selectedPoints ? [...selectedPoints] : []);
      assignAnnotationPositions(pts);
    };

    // The legend is drawn from dummy traces (getLegendTraces) that hold no
    // real points, so Plotly's default click behavior toggles the dummy and
    // nothing else: the entry greys out while the plot stays put. Returning
    // false suppresses that, leaving the legend a static key rather than a
    // control that appears to do something. Registered unconditionally — with
    // no legend shown (Data Explorer's case) these never fire.
    // TODO: make these actually filter, as Data Explorer's own legend does.
    on("plotly_legendclick", () => false);
    on("plotly_legenddoubleclick", () => false);

    // After initializing the plot with `autorange` set to true, store what
    // Plotly calculated for the axes zoom level and turn off autorange.
    on("plotly_afterplot", () => {
      // Add in shapes (y=x, regression lines) only after the plot has been
      // rendered. This prevents them from influencing the autorange
      // calculation.
      if (shapes !== plot.layout.shapes) {
        Plotly.update(plot, {}, { shapes });
        plot.layout.shapes = calcAutoscaleShapes(showIdentityLine, extents);
      }

      if (!axes.current.xaxis || !axes.current.yaxis) {
        axes.current = {
          xaxis: { ...plot.layout.xaxis, autorange: false },
          yaxis: { ...plot.layout.yaxis, autorange: false },
        };
      }

      moveSelectedPointsOnTopOfLines(plot);
    });

    on("plotly_relayout", () => {
      // A zoom/resize invalidates the pixel-space committed marquees, so drop
      // the additive (shift-select) state along with the stored axes.
      const cfg = (plot as any).__facetSel as FacetSelectionConfig | undefined;
      if (cfg) {
        cfg.committedShapes = [];
        cfg.selectionRegionKey = null;
      }

      axes.current = {
        xaxis: { ...plot.layout.xaxis, autorange: false },
        yaxis: { ...plot.layout.yaxis, autorange: false },
      };

      plot.layout.annotations?.forEach((annotation) => {
        const { ax, ay } = annotation;

        if (ax != null && ay != null) {
          const { pointIndex } = annotation as any;
          annotationTails.current[
            `${xKey}-${yKey}-${pointIndex}`
          ] = captureAnnotationTail(ax, ay, annotationFontSize);
        }
      });
    });

    on("plotly_click", (e: PlotMouseEvent) => {
      const { pointIndex, curveNumber } = e.points[0];
      const anyModifier =
        e.event.ctrlKey || e.event.metaKey || e.event.shiftKey;

      const index = isContinuousCurve(curveNumber)
        ? contTraceIndex[pointIndex]
        : pointIndex;

      if (onClickPoint) {
        onClickPoint(index, anyModifier);
      }

      // WORKAROUND: If you mean to double-click to zoom out and
      // select a point by accident, restore the previous selections.
      const prevAxes = axes.current;
      const prevSelection = selectedPoints;

      setTimeout(() => {
        if (axes.current !== prevAxes && prevSelection) {
          onMultiselect([...prevSelection]);
        }
      }, 100);
    });

    on("plotly_selecting", (e: PlotSelectionEvent) => {
      if (selectedPoints && selectedPoints.size > 0) {
        onClickResetSelection();
      }

      // enforceSingleFacetSelection (waterfall): the region anchor is deferred
      // until the drag box first touches a point (facetSelectionDragLayer leaves
      // selectionRegionKey null on a fresh drag), so the initial box isn't pinned
      // to whatever region the click happened to land in. Lock it here to the
      // x-rank region of the first contacted point nearest the drag start; from
      // then on the interceptor confines the box to that region.
      if (!useFacetSelection) {
        return;
      }
      const cfg = (plot as any).__facetSel as FacetSelectionConfig | undefined;
      if (
        !cfg ||
        cfg.regionModel.kind !== "ranges" ||
        cfg.selectionRegionKey != null || // already resolved (or shift-locked)
        !e?.points?.length // no contact yet
      ) {
        return;
      }
      const startCoord = (plot as any).__facetSelStartCoord as
        | number
        | undefined;
      let bestKey: FacetSelectionConfig["selectionRegionKey"] = null;
      let bestDist = Infinity;
      e.points.forEach((p) => {
        if (p.data.hoverinfo === "skip" || p.data.hoverinfo === "none") {
          return;
        }
        const origIndex = isContinuousCurve(p.curveNumber)
          ? contTraceIndex[p.pointIndex]
          : p.pointIndex;
        const xv = x[origIndex];
        const dist = startCoord == null ? 0 : Math.abs(xv - startCoord);
        if (dist < bestDist) {
          bestDist = dist;
          if (cfg.regionModel.kind === "ranges") {
            bestKey = resolveRange(cfg.regionModel.regions, xv);
          }
        }
      });
      if (bestKey != null) {
        cfg.selectionRegionKey = bestKey;
      }
    });

    on("plotly_selected", (e: PlotSelectionEvent) => {
      let points =
        e?.points
          .filter(
            (p) => p.data.hoverinfo !== "skip" && p.data.hoverinfo !== "none"
          )
          .map((p) => {
            return isContinuousCurve(p.curveNumber)
              ? contTraceIndex[p.pointIndex]
              : p.pointIndex;
          }) || [];

      // enforceSingleFacetSelection invariant: an out-of-region point must never
      // enter the selection set. The interceptor confines the live box, but a
      // fast drag from outside the resolved region (e.g. dragging back across the
      // anchor corner) can momentarily span a neighbour before clamping. Mirror
      // the density plot and drop any committed point that doesn't fall in the
      // anchor region, so the committed set is always exactly that region's.
      if (useFacetSelection) {
        const cfg = (plot as any).__facetSel as
          | FacetSelectionConfig
          | undefined;
        if (
          cfg?.selectionRegionKey != null &&
          cfg.regionModel.kind === "ranges"
        ) {
          const { regions } = cfg.regionModel;
          const anchorKey = cfg.selectionRegionKey;
          points = points.filter(
            (i) => resolveRange(regions, x[i]) === anchorKey
          );
        }
      }

      assignAnnotationPositions(points);
      onMultiselect(points);
    });

    on("plotly_deselect", () => {
      onClickResetSelection();
    });

    // WORKAROUND: Double-click is supposed to reset the zoom but it only works
    // actually intermittently so we'll do it ourselves.
    on("plotly_doubleclick", () => {
      zoom("reset");
    });

    // WORKAROUND: For some reason, autosize only works
    // with width so we'll calculate the height as well.
    on("plotly_autosize", () => {
      if (height === "auto") {
        const nextHeight = calcPlotHeight(plot);
        if (plot.layout.height !== nextHeight) {
          plot.layout.height = nextHeight;
          Plotly.redraw(plot);
        }
      }
    });

    // https://github.com/plotly/plotly.js/blob/55dda47/src/lib/prepare_regl.js
    on("plotly_webglcontextlost", () => {
      // Fixes a bug where points disappear after the browser has been left
      // idle for some time.
      Plotly.redraw(plot);
    });

    // See onWebglContextRestored: complements the handler above for the
    // case where the context is lost to eviction rather than idling —
    // there, nothing is drawable again until *this* fires.
    const stopWatchingContextRestore = onWebglContextRestored(plot, () => {
      Plotly.redraw(plot);
    });

    // Add a few non-standard methods to the plot for convenience.
    plot.setDragmode = (nextDragmode) => {
      const shouldResetSelection =
        plot?.layout?.dragmode !== nextDragmode &&
        (nextDragmode === "select" || nextDragmode === "lasso");

      if (shouldResetSelection && onClickResetSelection) {
        onClickResetSelection();
      }

      setDragmode(nextDragmode);
    };

    plot.zoomIn = () => zoom("in");
    plot.zoomOut = () => zoom("out");
    plot.resetZoom = () => zoom("reset");

    plot.getImageFigure = (options) => {
      if (!legendForDownload) {
        window.console.warn("`legendForDownload` is undefined");
        return null;
      }

      // Position comes from the export config, not from a plot style — see
      // legendLayoutFor. Called with no options (the SVG path, or any other
      // caller) it keeps today's right-hand placement.
      const legendPosition = options?.legendPosition ?? "right";
      const edgePadding = options?.edgePadding ?? EXPORT_EDGE_PADDING;
      const chromeLineWidth =
        options?.chromeLineWidth ?? DEFAULT_CHROME_LINE_WIDTH;
      const dataLineWidth = options?.dataLineWidth ?? DEFAULT_DATA_LINE_WIDTH;
      const xAxisLabelOverride = options?.xAxisLabel;
      const yAxisLabelOverride = options?.yAxisLabel;
      const exportLegend = applyLegendLabelOverrides(
        legendForDownload,
        options?.legendTitle,
        options?.legendItemLabels
      );

      const legendTraces = wantsLegendTraces(legendPosition)
        ? getLegendTraces(exportLegend, templateTrace)
        : [];

      const figure = cloneFigureForExport([...plot.data, ...legendTraces], {
        ...plot.layout,
        // The "inner" half of edgePadding: extra room between the tick
        // labels and the title itself, via the property Plotly actually
        // defines for that. `automargin` stays on (turning it off, tried
        // earlier, silently breaks how `_depth` is measured for the title,
        // asymmetrically between axes) — it just measures a bigger need now
        // that standoff asks for more, and sizes the margin to fit exactly
        // that, correctly, with nothing left to renegotiate.
        xaxis: {
          ...plot.layout.xaxis,
          ...calcChromeAxisOverrides(chromeLineWidth),
          // `hideXAxis` (waterfall, when faceted) sets `visible: false`
          // because the default "Rank" title is actively misleading once
          // there's more than one facet — rank position keeps climbing
          // across facet boundaries instead of restarting, so it can't be
          // read as a rank past the first one. The tick *values* have the
          // same problem regardless of what the title says, so a genuine
          // override (see ExportImageModal's own comment on why this is
          // never true for the untouched default text) brings back only
          // the title — ticks and their numbers stay off. This only makes
          // sense for `xAxisIsRank` (Waterfall): a real scatter plot's
          // x-axis is actual data, and those numbers must stay no matter
          // what the title says.
          ...(xAxisLabelOverride !== undefined && xAxisIsRank
            ? { visible: true, showticklabels: false, ticks: "" as const }
            : {}),
          title: {
            ...(plot.layout.xaxis as any)?.title,
            ...(xAxisLabelOverride !== undefined
              ? { text: xAxisLabelOverride }
              : {}),
            standoff:
              ((plot.layout.xaxis as any)?.title?.standoff ?? 8) + edgePadding,
          },
        },
        yaxis: {
          ...plot.layout.yaxis,
          ...calcChromeAxisOverrides(chromeLineWidth),
          title: {
            ...(plot.layout.yaxis as any)?.title,
            ...(yAxisLabelOverride !== undefined
              ? { text: yAxisLabelOverride }
              : {}),
            standoff:
              ((plot.layout.yaxis as any)?.title?.standoff ?? 0) + edgePadding,
          },
        },
        // Seeds the single-shot export render with the margin this instance
        // already converged to (see exportMarginFloor), plus the same
        // edgePadding the standoff above just asked the title to consume —
        // matching the new need exactly is what lets automargin converge in
        // its one pass rather than needing a second to catch up.
        //
        // This buys the *inner* half of "space around the label" only. The
        // "outer" half — room past the title before the image's true edge —
        // isn't available through margin at all: automargin always sizes to
        // fit exactly, with no slack by design, and asking for slack anyway
        // (a bigger floor with automargin on) triggers the exact
        // renegotiation this comment already warns about. See
        // ExportImageModal's own compositing step for how that half is
        // added instead — as an actual border on the finished image, where
        // Plotly's layout engine has no say.
        margin: exportMarginFloor(plot, {
          l: edgePadding,
          b: edgePadding,
        }),
        shapes: calcPlotIndicatorLineShapes(
          showIdentityLine,
          regressionLines,
          widenExtentsToAxisRange(
            extents,
            (plot.layout.xaxis as any)?.range,
            (plot.layout.yaxis as any)?.range
          ),
          false,
          { xref: "x", yref: "y" },
          dataLineWidth
        ),
        // The font follows the axis font size rather than a constant: at 14 it
        // is what this hardcoded, and it now scales with the rest (an export
        // sized for a slide was rendering its legend at ~7 pt, and this is the
        // only legend a reader of the file gets — ours is HTML and can't be
        // rasterized).
        ...legendLayoutFor(legendPosition, {
          title: exportLegend.title,
          fontSize: xAxisFontSize,
        }),
      });

      // A label dragged in ExportImageModal's own live "Adjust label
      // positions" preview — applied last so it always wins over whatever
      // ax/ay the live plot's own stored tail (captured above, via
      // annotationTails.current) resolved to.
      figure.layout.annotations = applyAnnotationTailOverrides(
        figure.layout.annotations as any[] | undefined,
        options?.annotationTailOverrides,
        annotationFontSize
      );

      return figure;
    };

    plot.downloadImage = (options) => {
      const figure = plot.getImageFigure();

      if (figure) {
        Plotly.downloadImage(figure, options);
      }
    };

    plot.isPointInView = (pointIndex: number) => {
      const px = x[pointIndex] as number;
      const py = y[pointIndex] as number;
      const xrange = plot.layout.xaxis.range as [number, number];
      const yrange = plot.layout.yaxis.range as [number, number];

      return (
        px >= xrange[0] && px <= xrange[1] && py >= yrange[0] && py <= yrange[1]
      );
    };

    plot.xValueMissing = (pointIndex: number) => {
      return typeof data[xKey][pointIndex] !== "number";
    };

    plot.yValueMissing = (pointIndex: number) => {
      return typeof data[yKey][pointIndex] !== "number";
    };

    return () => {
      listeners.forEach(([eventName, callback]) =>
        plot.removeListener?.(eventName, callback)
      );
      stopWatchingContextRestore();
    };
  }, [
    data,
    xKey,
    yKey,
    colorKey1,
    colorKey2,
    categoricalColorKey,
    continuousColorKey,
    contLegendKeys,
    colorMap,
    xLabel,
    yLabel,
    legendForDownload,
    hoverTextKey,
    annotationTextKey,
    height,
    selectedPoints,
    pointsToAnnotate,
    selectionCount,
    hasFacetOptionsEnabled,
    onClickPoint,
    onMultiselect,
    onClickResetSelection,
    pointVisibility,
    dragmode,
    extents,
    showIdentityLine,
    regressionLines,
    outlineUnselectedPoints,
    customHoverinfo,
    hideXAxis,
    hideXAxisGrid,
    xAxisIsRank,
    showBuiltinLegend,
    pointSize,
    pointOpacity,
    outlineWidth,
    palette,
    annotationFontSize,
    xAxisFontSize,
    yAxisFontSize,
    enforceSingleFacetSelection,
    selectionRegions,
    selectionAxis,
    shapes,
    Plotly,
  ]);

  return <div className={styles.ScatterPlot} ref={ref} />;
}

export default function LazyPrototypeScatterPlot({
  data,
  ...otherProps
}: Props) {
  const PlotlyLoader = usePlotlyLoader();

  return (
    <PlotlyLoader version="module">
      {(Plotly) =>
        data ? (
          <PrototypeScatterPlot data={data} Plotly={Plotly} {...otherProps} />
        ) : null
      }
    </PlotlyLoader>
  );
}
